import React, { useCallback, useRef } from "react";
import { CircularProgress, Typography } from "@mui/joy";
import { useTranslation } from "react-i18next";
import { OpenInFullRounded } from "@mui-symbols-material/w300";
import { ActionsMenu } from "@dreebit-registry/vsm-microservice-components";
import { ActionItem, WidgetActionsMenu } from "../WidgetActionsMenu";
import AssetLocationsMap, { MapViewState } from "./AssetLocationsMap";
import { useAssetMapFullscreen } from "./useAssetMapFullscreen";
import { MapBounds } from "../../utils/mapBounds";

export type { MapViewState };

interface MapWidgetProps {
  onDelete: () => void;
  onEdit: () => void;
}

const MapWidget: React.FC<MapWidgetProps> = ({ onDelete, onEdit }) => {
  const { t } = useTranslation();
  const { open: openFullscreen } = useAssetMapFullscreen();
  // The widget's current view, so the full-screen map opens on the same area.
  const viewBoundsRef = useRef<MapBounds>();

  const handleViewChange = useCallback((state: MapViewState) => {
    viewBoundsRef.current = state.bounds;
  }, []);

  const actionItems: ActionItem[] = [
    {
      item: (
        <>
          <OpenInFullRounded fontSize="inherit" sx={{ mr: "4px" }} />
          {t("Full screen")}
        </>
      ),
      onClick: () => openFullscreen(viewBoundsRef.current),
      active: true,
    },
    ...WidgetActionsMenu(onDelete, onEdit),
  ];

  return (
    <AssetLocationsMap
      onViewChange={handleViewChange}
      renderOverlay={({ assetCount, loading }) => (
        // Drag handle overlay - sits on top of the map, captures drag events
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            padding: "8px 4px 8px 10px",
            zIndex: 1000,
            cursor: "grab",
          }}
        >
          <Typography
              level="titleSmallLight"
              sx={{
                flex: 1,
                pl: "12px",
                textAlign: "left",
                color: "#333",
                textShadow: "0 0 4px rgba(255,255,255,0.8)",
              }}
          >
            {t("Asset Locations")}{assetCount != null ? ` (${assetCount})` : ""}
          </Typography>
          {loading && (
            <div
              style={{
                background: "rgba(255,255,255,0.8)",
                borderRadius: "50%",
                padding: 4,
                display: "flex",
              }}
            >
              <CircularProgress size="sm" />
            </div>
          )}
          <div className="no-drag" style={{ paddingRight: "4px" }}>
            <ActionsMenu items={actionItems} />
          </div>
        </div>
      )}
    />
  );
};

export default MapWidget;
